import React, { useState, useEffect, useContext } from 'react';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { getAppointmentSettings } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Calendar, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Alert, AlertDescription } from "./ui/alert";

const CalendarAuthorizationList = () => {
  const { organizationId } = useContext(OrganizationContext);
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await getAppointmentSettings(organizationId);
        setSettings(data);
        setError(null);
      } catch (error) {
        console.error('Error fetching appointment settings:', error);
        setError('Failed to load calendar authorizations');
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchSettings();
    }
  }, [organizationId]);

  const getCalendarIdentifier = (setting) => {
    switch (setting.calendarProvider) {
      case 'GOOGLE':
        return setting.googleCalendarId || 'Not configured';
      case 'CALENDLY':
        return setting.calendlySchedulingUrl || 'Not configured';
      case 'CALCOM':
        return setting.calcomSchedulingUrl || 'Not configured';
      default:
        return 'Unknown provider';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-16">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Current Calendar Authorizations
        </CardTitle>
        <CardDescription>
          Calendar configurations for this organization
        </CardDescription>
      </CardHeader>
      <CardContent>
        {settings.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No calendar authorizations found
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event Name</TableHead>
                <TableHead>Calendar Provider</TableHead>
                <TableHead>Calendar Identifier</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settings.map((setting, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{setting.eventName || 'Not set'}</TableCell>
                  <TableCell>{setting.calendarProvider}</TableCell>
                  <TableCell className="max-w-md truncate">
                    {getCalendarIdentifier(setting)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarAuthorizationList;